import { Decimal, ZERO, differsBeyondTolerance, roundToCurrencyPrecision } from '@insio/decimal';
import type { CurrencyCode } from '@insio/shared-types';

export interface InvoiceTotalsExpectationInput {
  totalAmountNoVat: Decimal;
  totalVatAmount: Decimal;
  totalAmountInclVat: Decimal;
  /** null counts as 0, mirroring the validator's `roundingAmount ?? 0`. */
  roundingAmount: Decimal | null;
  currencyCode?: CurrencyCode;
}

export interface InvoiceTotalsExpectation {
  /** noVat + VAT + rounding, rounded to the currency's precision. */
  expectedInclVat: Decimal;
  /** True when the entered gross total disagrees with `expectedInclVat`. */
  hasMismatch: boolean;
}

/**
 * Cross-check for the invoice totals editor: derives the gross total the three
 * other amounts add up to, driving the "Očekáváno" hint next to the gross input.
 *
 * The gross total is the dependent variable — the same side the backend's
 * `storedTotalsVsCalcFromLines` rule puts `roundingAmount` on. The net and VAT
 * amounts are deliberately never back-solved: subtraction there yields
 * sign-flipped values that violate the `totalAmountLessThanNoVat` rule.
 */
export function computeInvoiceTotalsExpectation({
  totalAmountNoVat,
  totalVatAmount,
  totalAmountInclVat,
  roundingAmount,
  currencyCode
}: InvoiceTotalsExpectationInput): InvoiceTotalsExpectation {
  // Round before comparing so the displayed, compared and applied value is one value.
  const expectedInclVat = roundToCurrencyPrecision(
    totalAmountNoVat.plus(totalVatAmount).plus(roundingAmount ?? ZERO),
    currencyCode
  );
  // Signed comparison: credit notes sum correctly with their signs, and a sign
  // flip between the parts and the total is a real defect worth surfacing.
  return {
    expectedInclVat,
    hasMismatch: differsBeyondTolerance(totalAmountInclVat, expectedInclVat)
  };
}
